import { create } from 'zustand';
import { message } from 'antd';
import { ddddAlertApi } from '../utils/ddddAlertApi';
import { 
  NotificationChannel, 
  CreateChannelRequest, 
  UpdateChannelRequest,
  TestNotificationRequest 
} from '../types/auth';

interface ChannelState {
  // 状态
  channels: NotificationChannel[];
  loading: boolean;
  error: string | null;
  
  // 模态框状态
  isFormModalVisible: boolean;
  isTestModalVisible: boolean;
  editingChannel: NotificationChannel | null;
  testingChannel: NotificationChannel | null;
  
  // 操作方法
  fetchChannels: () => Promise<void>;
  createChannel: (data: CreateChannelRequest) => Promise<boolean>;
  updateChannel: (id: string, data: UpdateChannelRequest) => Promise<boolean>;
  deleteChannel: (id: string) => Promise<boolean>;
  testChannel: (id: string, testData: TestNotificationRequest) => Promise<boolean>;
  toggleChannelStatus: (id: string, isEnabled: boolean) => Promise<boolean>;
  
  // 模态框控制方法
  openFormModal: (channel?: NotificationChannel) => void;
  closeFormModal: () => void;
  openTestModal: (channel: NotificationChannel) => void;
  closeTestModal: () => void;
  
  // 工具方法
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export const useChannelStore = create<ChannelState>((set, get) => ({
  // 初始状态
  channels: [],
  loading: false,
  error: null,
  isFormModalVisible: false,
  isTestModalVisible: false,
  editingChannel: null,
  testingChannel: null,

  // 获取渠道列表
  fetchChannels: async () => {
    set({ loading: true, error: null });
    try {
      const response = await ddddAlertApi.getChannels();
      if (response.success && response.data) {
        set({
          channels: response.data,
          loading: false
        });
      } else {
        throw new Error('获取渠道列表失败');
      }
    } catch (error: any) {
      console.error('Failed to fetch channels:', error);
      const errorMessage = error.message || '获取渠道列表失败';

      // 如果是认证错误，提示用户重新登录
      if (errorMessage.includes('Invalid access token') || errorMessage.includes('INVALID_TOKEN')) {
        message.error('登录已过期，请重新连接钱包进行认证');
        // 可以在这里触发重新认证逻辑
      } else {
        message.error(errorMessage);
      }

      set({
        error: errorMessage,
        loading: false
      });
    }
  },

  // 创建渠道
  createChannel: async (data: CreateChannelRequest) => {
    set({ loading: true, error: null });
    try {
      const response = await ddddAlertApi.createChannel(data);
      if (response.success) {
        message.success('渠道创建成功');
        // 刷新列表
        await get().fetchChannels();
        set({ loading: false });
        return true;
      } else {
        throw new Error('创建渠道失败');
      }
    } catch (error: any) {
      console.error('Failed to create channel:', error);
      const errorMessage = error.message || '创建渠道失败';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      message.error(errorMessage);
      return false;
    }
  },

  // 更新渠道
  updateChannel: async (id: string, data: UpdateChannelRequest) => {
    set({ loading: true, error: null });
    try {
      const response = await ddddAlertApi.updateChannel(id, data);
      if (response.success) {
        message.success('渠道更新成功');
        // 刷新列表
        await get().fetchChannels();
        set({ loading: false });
        return true;
      } else {
        throw new Error('更新渠道失败');
      }
    } catch (error: any) {
      console.error('Failed to update channel:', error);
      const errorMessage = error.message || '更新渠道失败';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      message.error(errorMessage);
      return false;
    }
  },

  // 删除渠道
  deleteChannel: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await ddddAlertApi.deleteChannel(id);
      if (response.success) {
        message.success('渠道删除成功');
        // 刷新列表
        await get().fetchChannels();
        set({ loading: false });
        return true;
      } else {
        throw new Error('删除渠道失败');
      }
    } catch (error: any) {
      console.error('Failed to delete channel:', error);
      const errorMessage = error.message || '删除渠道失败';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      message.error(errorMessage);
      return false;
    }
  },

  // 测试渠道
  testChannel: async (id: string, testData: TestNotificationRequest) => {
    set({ loading: true, error: null });
    try {
      const response = await ddddAlertApi.testChannel(id, testData);
      if (response.success) {
        message.success('测试消息发送成功');
        set({ loading: false });
        return true;
      } else {
        throw new Error('测试消息发送失败');
      }
    } catch (error: any) {
      console.error('Failed to test channel:', error);
      const errorMessage = error.message || '测试消息发送失败';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      message.error(errorMessage);
      return false;
    }
  },

  // 切换渠道状态
  toggleChannelStatus: async (id: string, isEnabled: boolean) => {
    try {
      const response = await ddddAlertApi.updateChannel(id, { is_enabled: isEnabled });
      if (response.success) {
        message.success(`渠道已${isEnabled ? '启用' : '禁用'}`);
        // 更新本地状态
        set(state => ({
          channels: state.channels.map(channel =>
            channel.id === id ? { ...channel, is_enabled: isEnabled } : channel
          )
        }));
        return true;
      } else {
        throw new Error('状态更新失败');
      }
    } catch (error: any) {
      console.error('Failed to toggle channel status:', error);
      const errorMessage = error.message || '状态更新失败';
      message.error(errorMessage);
      return false;
    }
  },

  // 模态框控制方法
  openFormModal: (channel?: NotificationChannel) => {
    set({ 
      isFormModalVisible: true, 
      editingChannel: channel || null 
    });
  },

  closeFormModal: () => {
    set({ 
      isFormModalVisible: false, 
      editingChannel: null 
    });
  },

  openTestModal: (channel: NotificationChannel) => {
    set({ 
      isTestModalVisible: true, 
      testingChannel: channel 
    });
  },

  closeTestModal: () => {
    set({ 
      isTestModalVisible: false, 
      testingChannel: null 
    });
  },

  // 工具方法
  clearError: () => {
    set({ error: null });
  },

  setLoading: (loading: boolean) => {
    set({ loading });
  },
}));
